import { useParams, useNavigate } from "react-router-dom";
import { comics, comicTags } from "@/mocks/comics";
import ComicCard from "@/components/feature/ComicCard";

export default function ComicDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <div className="container-x flex flex-col items-center py-24 text-center">
        <i className="ri-error-warning-line text-5xl text-foreground-300"></i>
        <p className="mt-4 font-heading text-xl font-bold text-foreground-700">这部漫画不存在或已下架</p>
        <button onClick={() => navigate("/category")} className="mt-5 btn-primary">返回漫画列表</button>
      </div>
    );
  }

  const related = comics.filter((c) => c.tag === comic.tag && c.id !== comic.id).slice(0, 5);
  const chapters = Array.from({ length: Math.min(comic.chapters, 10) }, (_, i) => comic.chapters - i);

  return (
    <div>
      <section className="w-full bg-background-100">
        <div className="container-x py-10">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
            <button onClick={() => navigate("/")} className="cursor-pointer hover:text-primary-600">首页</button>
            <i className="ri-arrow-right-s-line"></i>
            <button onClick={() => navigate("/category")} className="cursor-pointer hover:text-primary-600">全部漫画</button>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">{comic.tag}漫画</span>
          </nav>

          <div className="mt-6 flex flex-col gap-6 sm:flex-row">
            {/* 封面 */}
            <div className="mx-auto w-56 shrink-0 sm:mx-0 sm:w-60">
              <div className="aspect-[3/4] w-full overflow-hidden rounded-lg border border-background-300/60">
                <img src={comic.cover} alt={`${comic.title}古风漫画封面`} title={`${comic.title} - ${comic.tag}古风国漫`} className="h-full w-full object-cover object-top" />
              </div>
              <div className="mt-3 flex gap-2">
                <button className="btn-primary flex-1">
                  <i className="ri-bookmark-line mr-1"></i>加入收藏
                </button>
                <button className="flex h-11 w-11 items-center justify-center rounded-md border border-foreground-300 text-foreground-700 hover:border-primary-500 hover:text-primary-600 cursor-pointer" aria-label="分享">
                  <i className="ri-share-line"></i>
                </button>
              </div>
            </div>

            {/* 信息 */}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="tag-chip">{comic.tag}</span>
                {comic.isHot && <span className="tag-chip !bg-primary-100 !text-primary-700">热播</span>}
                {comic.isNew && <span className="tag-chip !bg-accent-100 !text-accent-800">新番</span>}
                <span className="tag-chip">{comic.status}</span>
              </div>
              <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">{comic.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
                <span>作者：<span className="font-semibold text-foreground-800">{comic.author}</span></span>
                <span className="flex items-center gap-1 text-accent-700">
                  <i className="ri-star-fill text-accent-500"></i>{comic.rating} 分
                </span>
                <span>{comic.chapters} 话</span>
              </div>

              <p className="mt-5 max-w-3xl text-sm leading-relaxed text-foreground-700">{comic.intro}</p>

              <div className="mt-5 grid max-w-md grid-cols-3 gap-3">
                <div className="rounded-lg border border-background-300/60 bg-background-50 p-3 text-center">
                  <p className="text-lg font-bold text-primary-600">{comic.views}</p>
                  <p className="text-xs text-foreground-500">人气</p>
                </div>
                <div className="rounded-lg border border-background-300/60 bg-background-50 p-3 text-center">
                  <p className="text-lg font-bold text-accent-600">{comic.favorites}</p>
                  <p className="text-xs text-foreground-500">收藏</p>
                </div>
                <div className="rounded-lg border border-background-300/60 bg-background-50 p-3 text-center">
                  <p className="text-lg font-bold text-secondary-600">{comic.chapters}</p>
                  <p className="text-xs text-foreground-500">章节</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container-x py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* 章节列表 */}
          <section className="lg:col-span-2">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <span className="h-px w-5 bg-primary-500"></span>最近更新
            </h2>
            <div className="mt-4 overflow-hidden rounded-lg border border-background-300/60 bg-background-50">
              {chapters.map((ch, idx) => (
                <div
                  key={ch}
                  className={`flex items-center justify-between border-b border-background-200 px-5 py-3.5 text-sm last:border-0 ${
                    idx === 0 ? "bg-primary-100/60" : ""
                  }`}
                >
                  <span className={`flex items-center gap-2 ${idx === 0 ? "font-semibold text-primary-700" : "text-foreground-700"}`}>
                    {idx === 0 && <i className="ri-fire-fill text-primary-500"></i>}
                    第{ch}话 {idx === 0 ? comic.latestChapter.replace(/第\d+话\s*/, "") : ""}
                  </span>
                  <span className="text-xs text-foreground-500">{idx === 0 ? comic.updateTime : `更新于 ${comic.updateTime}`}</span>
                </div>
              ))}
            </div>
            <button className="mt-4 w-full rounded-md border border-foreground-300 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-500 hover:text-primary-600 cursor-pointer">
              查看全部 {comic.chapters} 话
            </button>
          </section>

          {/* 简介 + 相关 */}
          <aside>
            <div className="rounded-lg border border-background-300/60 bg-background-100 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">作品简介</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">{comic.intro}</p>
              <div className="mt-4 space-y-2 text-sm text-foreground-600">
                <p className="flex justify-between"><span>题材</span><span className="font-semibold text-foreground-800">{comic.tag}</span></p>
                <p className="flex justify-between"><span>作者</span><span className="font-semibold text-foreground-800">{comic.author}</span></p>
                <p className="flex justify-between"><span>状态</span><span className="font-semibold text-foreground-800">{comic.status}</span></p>
                <p className="flex justify-between"><span>更新</span><span className="font-semibold text-foreground-800">{comic.updateTime}</span></p>
              </div>
            </div>
          </aside>
        </div>

        {/* 相关推荐 */}
        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <span className="h-px w-5 bg-primary-500"></span>同类作品推荐
            </h2>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
              {related.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}